import { useState, useContext, useEffect } from "react";
import styles from "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import Draggable from "react-draggable";
import {
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
  ConversationHeader,
  Avatar,
} from "@chatscope/chat-ui-kit-react";
import AIApi from "../api/AIApi";
import { AuthContext } from "../context/AuthContext";

const processMarkdownAndHtml = (model, identifierCases = []) => {
  const processedContent = model.message.replace(/\n/g, "<br />");

  const htmlContent = processedContent
    .replace(/###\s?(.*?)(<br \/>|$)/g, "<strong>$1</strong><br>")
    .replace(/##\s?(.*?)(\n|$)/g, "<h2>$1</h2>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/(\d{2}FF-\d{6}-[A-Z]{2})/g, (match) => {
      const uweEntity = identifierCases.find((c) => c.number === match);
      return uweEntity
        ? `<a href="#" data-id="${uweEntity.id}" data-slug="${uweEntity.slug}" class="case-link">${match}</a>`
        : match;
    });

  return htmlContent;
};

function FormattedMessage({ model, identifierCases, history }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.target.classList.contains("case-link")) {
        e.preventDefault();
        const id = e.target.dataset.id;
        const slug = e.target.dataset.slug;
        history.push(`/workflow/${slug}/${id}`);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [history, model]);

  return (
    <Message model={model}>
      <Message.CustomContent>
        <div
          className="markdown-content"
          style={{ whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{
            __html: processMarkdownAndHtml(model, identifierCases),
          }}
        />
      </Message.CustomContent>
    </Message>
  );
}

function ChatApp() {
  const { user } = useContext(AuthContext);
  if (!user) return null;
  if(user?.user?.accessProfile?.name !== "systemadmin" && user?.user?.accessProfile?.name !== "administrator") return null;
  const history = useHistory();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [identifierCases, setIdentifierCases] = useState();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleSend = async (message) => {
    setLoading(true);
    const newMessage = {
      message,
      sentTime: "just now",
      sender: "You",
      direction: "outgoing",
    };
    setMessages([...messages, newMessage]);
    setInput("");

    try {
      const response = await AIApi.chat({
        content: input,
        messages,
        conversationId,
      });
      const aiResponse = response?.messages?.slice(-1)[0];
      if (aiResponse) {
        setMessages((prev) => [
          ...prev,
          {
            message: aiResponse.content,
            sentTime: "just now",
            sender: "AI",
            direction: "incoming",
          },
        ]);
        setConversationId(response.conversationId);
        setIdentifierCases(response.identifierCases);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className="chat-app">
      {isOpen ? (
        <Draggable
          bounds={{
            bottom: 0,
          }}
          cancel=".no-drag"
        >
          <div className="div-chat-content">
            <ChatContainer>
              <ConversationHeader>
                <Avatar className="avatar">
                  <FontAwesomeIcon icon={faRobot} size="lg" />
                </Avatar>
                <ConversationHeader.Actions>
                  <div className="align-right">
                    {messages?.length > 0 ? (
                      <Button
                        color="link"
                        title="Clean Context"
                        onClick={() => {
                          setMessages([]);
                          setConversationId("");
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    ) : null}
                    <Button
                      color="link"
                      title="Close"
                      onClick={() => setIsOpen(false)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                </ConversationHeader.Actions>
              </ConversationHeader>

              <MessageList
                className="no-drag"
                typingIndicator={
                  loading ? <TypingIndicator content="IA is typing" /> : null
                }
              >
                {messages.map((msg, index) => (
                  <FormattedMessage
                    key={index}
                    model={msg}
                    identifierCases={identifierCases}
                    history={history}
                  />
                ))}
              </MessageList>
              <MessageInput
                className="no-drag"
                placeholder="Type message here"
                value={input}
                attachButton={false}
                disabled={loading}
                onChange={(val) => {
                  setInput(val);
                }}
                onSend={handleSend}
              />
            </ChatContainer>
          </div>
        </Draggable>
      ) : (
        <Button
          className="open-chat-button"
          color="primary"
          onClick={() => setIsOpen((p) => !p)}
        >
          <FontAwesomeIcon icon={faRobot} size="lg" />
        </Button>
      )}
    </div>
  );
}

export default ChatApp;
